import { useState } from "react";
import { Mail, Phone, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageBanner } from "@/pages/Listings";
import { GoldButton } from "@/components/Cards";
import { trackEvent } from "@/lib/firebase";
import { HeadquartersCards, SocialLinks } from "@/components/SiteIdentity";

const CATS = ["General Inquiry", "Partnership", "Media", "Membership", "Investment", "Organization Registration", "Technical Support"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", organization: "", subject: "", category: "General Inquiry", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      trackEvent("contact_form_submit", { category: form.category });
      toast.success("Message sent! We'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", subject: "", category: "General Inquiry", message: "" });
    } catch { toast.error("Failed to send. Please try again."); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-dir-bg" data-testid="contact-page">
      <PageBanner label="Contact" title="Get in Touch" desc="Reach out for partnerships, media, membership, investment or general inquiries." />
      <div className="mx-auto max-w-[1400px] px-4 py-14 sm:px-6 lg:px-10">
        <div className="grid gap-12 lg:grid-cols-3">
          <div className="space-y-6">
            {[[Mail, "Email", "info@dirnation.org"], [Phone, "Global Desk", "Available 24/7 online"]].map(([Icon, label, val], i) => (
              <div key={i} className="rounded-2xl border border-dir-gold/15 bg-dir-card p-6" data-testid={`contact-info-${i}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-dir-gold/30 bg-dir-gold/10"><Icon className="h-5 w-5 text-dir-gold" /></div>
                <div className="mt-4 text-[11px] uppercase tracking-wider text-dir-faint">{label}</div>
                <div className="mt-1 text-sm text-dir-text">{val}</div>
              </div>
            ))}
            <SocialLinks location="contact" />
          </div>

          <form onSubmit={submit} className="lg:col-span-2 rounded-2xl border border-dir-gold/15 bg-dir-card p-8 space-y-5" data-testid="contact-form">
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Name" value={form.name} onChange={set("name")} testId="contact-name" required />
              <Field label="Email" type="email" value={form.email} onChange={set("email")} testId="contact-email" required />
              <Field label="Organization" value={form.organization} onChange={set("organization")} testId="contact-org" />
              <div>
                <label className="mb-2 block text-sm text-dir-muted">Category</label>
                <select data-testid="contact-category" value={form.category} onChange={set("category")} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-3 text-sm text-dir-text focus:border-dir-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold">
                  {CATS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <Field label="Subject" value={form.subject} onChange={set("subject")} testId="contact-subject" />
            <div>
              <label className="mb-2 block text-sm text-dir-muted">Message</label>
              <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-3 text-sm text-dir-text focus:border-dir-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold" />
            </div>
            <GoldButton data-testid="contact-submit" disabled={loading}>{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Message"}</GoldButton>
          </form>
        </div>
        <section className="mt-14"><h2 className="mb-6 font-display text-2xl font-bold text-dir-text">DIR Nation Headquarters & Regional Hubs</h2><HeadquartersCards /></section>
      </div>
    </div>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm text-dir-muted">{label}</label>
      <input data-testid={testId} {...props} className="w-full rounded-xl bg-dir-elevated border border-dir-gold/20 px-4 py-3 text-sm text-dir-text placeholder:text-dir-faint focus:border-dir-gold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-dir-gold" />
    </div>
  );
}
